<template>
  <v-row>
    <v-row>
      <v-col class="align-left">
        <ExpandCollapseToggle
          id="subStructPanelToggle"
          @toggleAllPanels="toggleAllPanels"
          styleClass="mt-3"
          :panel="panel"
        />
      </v-col>
      <v-col class="align-right mt-2">
        <ValidationCountsHeader v-if="moduleType == MODULE_TYPES.MANAGEMENT" />
      </v-col>
    </v-row>
    <v-expansion-panels v-model="panel" multiple color="#fff">
      <v-expansion-panel class="mt-5">
        <v-expansion-panel-title
          class="d-flex justify-start"
          hide-actions
          expand-icon="fa-solid fa-plus"
          collapse-icon="fa-solid fa-minus"
        >
          <v-icon v-if="!panel.includes(0)" icon="fa-solid fa-plus" />
          <v-icon v-if="panel.includes(0)" icon="fa-solid fa-minus" />
          <h3>Substructure Conditions</h3>
        </v-expansion-panel-title>

        <v-expansion-panel-text eager>
          <v-form ref="subConditions" v-model="validSubConditions">
            <LabelSelect
              id="substructureConditionAndRating"
              showDurationIcon
              :tooltip="
                getHistDuration(substructureValue?.subRatingDuration, substructureValue?.subRatingDurationChanges)
              "
              @update:modelValue="
                {
                  handleUpdate();
                  substructureConditionUpdated = true;
                  conditionUpdated = true;
                }
              "
              label="B.C.03 Substructure Condition Rating"
              :refTable="REFERENCE_TABLE.CONDITION_RATING"
              :isEditable="isEditable"
              isRequired
              v-model="substructureValue.subRating"
            ></LabelSelect>
            <CommentTypeTextField
              v-if="moduleType == MODULE_TYPES.INSPECTION"
              label="Substructure Notes"
              id="field_substructure_Notes"
              :inspectionCommentType="INSPECTION_COMMENTS.SUBSTRUCTURE"
              :readonly="!isEditable"
              :counter="commentsMaxLength"
              :maxlength="commentsMaxLength"
              :singleLine="false"
              @changesDetected="handleUpdate()"
            />
            <ManagementCommentTypeTextField
              v-else
              label="Substructure Notes"
              id="field_substructure_Notes"
              :inspectionCommentType="INSPECTION_COMMENTS.SUBSTRUCTURE"
              :readonly="!isEditable"
              :counter="commentsMaxLength"
              :maxlength="commentsMaxLength"
              :comments="substructureValue.inspectionComments"
              :brkey="substructureValue.brkey"
              :inspectionId="substructureValue.inspectionId"
              @updateComment="(comment) => setUpdatedComment(comment, INSPECTION_COMMENTS.SUBSTRUCTURE)"
            />
          </v-form>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <AbutmentComponent
      ref="abutmentNearRef"
      id="near_Abutment_Component"
      abutmentType="Near"
      :unitKey="nearAbutmentSubstructureUnitKey"
      :isExpanded="isExpanded"
      :isEditable="isEditable"
      :moduleType="moduleType"
    />
    <AbutmentComponent
      ref="abutmentFarRef"
      id="far_Abutment_Component"
      abutmentType="Far"
      :isEditable="isEditable"
      :unitKey="farAbutmentSubstructureUnitKey"
      :isExpanded="isExpanded"
      :moduleType="moduleType"
    />
    <v-expansion-panels v-model="pierPanel" multiple color="#fff">
      <v-expansion-panel>
        <v-expansion-panel-title
          class="d-flex justify-start"
          hide-actions
          expand-icon="fa-solid fa-plus"
          collapse-icon="fa-solid fa-minus"
        >
          <v-icon v-if="!pierPanel.includes(0)" icon="fa-solid fa-plus" />
          <v-icon v-if="pierPanel.includes(0)" icon="fa-solid fa-minus" />
          <h3>Piers</h3>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row align="center">
            <v-col>
              <v-label class="noRecordsMessage d-flex justify-center" v-if="!piers || piers?.length == 0"
                >This structure currently contains no Piers.
              </v-label>
            </v-col>
          </v-row>
          <v-row class="pa-0">
            <v-col>
              <div v-for="pier in piers" :key="pier">
                <v-lazy :min-height="39" :options="{ threshold: 0.1 }">
                  <v-row>
                    <v-col class="pa-0">
                      <PiersSubstructure :moduleType="moduleType" :isEditable="isEditable" :pier="pier" />
                    </v-col>
                  </v-row>
                </v-lazy>
              </div>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-row>
</template>
<script>
import { computed, ref, watch, onMounted } from "vue";
import { ENV_CONFIG_PROPERTY } from "@/constants/EnvConfigProperties";
import { useConfigStore } from "@/stores/config";
import { useInspectionStore } from "@/stores/inspection";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { INSPECTION_COMMENTS, SUB_STRUCTURE_UNIT_KEY } from "@/constants/Inspections";
import CommentTypeTextField from "@/components/common/CommentTypeTextField.vue";
import LabelSelect from "@/components/common/LabelSelect.vue";
import AbutmentComponent from "@/components/inspection/substructureComponents/AbutmentComponent.vue";
import PiersSubstructure from "@/components/inspection/substructureComponents/PiersSubstructure.vue";
import { getHistDuration } from "@/composables/util";
import ExpandCollapseToggle from "@/components/shared/ExpandCollapseToggle.vue";
import { SNACKBAR_MARKER } from "@/constants/GlobalSnackbar.js";
import { useSubstructureStore } from "@/components/inspection/store/substructure";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { storeToRefs } from "pinia";
import { useInspectionLinksStore } from "@/stores/inspectionLinks";
import { useUserPermissionStore } from "@/stores/userPermission";
import { INSPECTION_LINKS_SUBSTRUCTURE_PERMISSIONS } from "@/rbac";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import ManagementCommentTypeTextField from "@/components/common/ManagementCommentTypeTextField.vue";
import ValidationCountsHeader from "@/components/shared/ValidationCountsHeader.vue";
import { useNstmDetailsStore } from "@/components/inspection/store/nstmFatigueDetails";
import { valueInRangeRule } from "@/composables/validationRules";

export default {
  name: "InspectionSubstructure",
  props: {
    moduleType: { default: MODULE_TYPES.INSPECTION },
  },
  setup(props) {
    //Common Code

    let abutmentNearRef = ref(null);

    let abutmentFarRef = ref(null);

    let valid = ref(true);

    let validSubConditions = ref(true);

    let subConditions = ref(null);

    const configStore = useConfigStore();
    const substructureStore = useSubstructureStore();
    const quickSearchStore = useQuickSearchStore();
    const structureHeader = computed(() => quickSearchStore.getStructure());
    const { hasQuickSearchSucceeded } = storeToRefs(quickSearchStore);
    const inspectionStore = useInspectionStore();
    const isExpanded = ref(true);
    const inspectionLinksStore = useInspectionLinksStore();
    const userPermissionStore = useUserPermissionStore();
    const nstmFatigueDetailStore = useNstmDetailsStore();

    const isEditable = computed(() => {
      if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
        return hasQuickSearchSucceeded.value && substructureValue.value?.brkey && hasAccessToEdit.value;
      } else {
        return inspectionStore.isEditable;
      }
    });

    const loadNstmData = () => {
      nstmFatigueDetailStore.setNstmFatigueValue({
        brkey: substructureValue.value.brkey,
        inspectionFCs: substructureValue.value.inspectionFCs,
        locations: substructureValue.value.locations,
        inspectionId: substructureValue.value.inspectionId,
      });
    };

    const hasAccessToEdit = computed({
      get() {
        if (structureHeader.value?.INSPECTIONSTATUS === INSPECTION_STATUSES.ACCEPTED) {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_SUBSTRUCTURE_PERMISSIONS[ACCESS_LEVELS.CUSTOM.EDIT_APPROVED_INSPECTION],
            userPermissionStore.getUserPermissionByBrkeyFromCache(substructureValue.value.brkey),
          );
        } else {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_SUBSTRUCTURE_PERMISSIONS[ACCESS_LEVELS.EDIT],
            userPermissionStore.getUserPermissionByBrkeyFromCache(substructureValue.value.brkey),
          );
        }
      },
    });

    let structure = computed(() => inspectionStore.selectedInspection);
    let substructureValue = computed(() => substructureStore.pageData);

    onMounted(async () => {
      await readData();
    });

    const readData = async () => {
      substructureStore.clearPageData();
      if (props.moduleType == MODULE_TYPES.MANAGEMENT && structureHeader.value?.BRKEY) {
        if (!hasQuickSearchSucceeded.value) {
          return;
        }
        await substructureStore.loadDataObject(
          props.moduleType,
          structureHeader.value.BRKEY,
          structureHeader.value.INSPECTIONID,
        );
        inspectionLinksStore.isDirty = false;
      } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
        await substructureStore.loadDataObject(props.moduleType, null, null, structure.value);
      }
      if (piers.value?.length > 0) {
        pierPanel.value = [0];
      } else {
        pierPanel.value = [];
      }
      loadNstmData();
    };
    const saveChanges = async () => {
      const isSuccess = await substructureStore.saveData(props.moduleType, substructureValue.value, structure.value);
      if (isSuccess) {
        if (props.moduleType == MODULE_TYPES.INSPECTION) {
          inspectionStore.setLowestConditionRating();
          await substructureStore.loadDataObject(props.moduleType, null, null, structure.value);
        } else {
          inspectionLinksStore.isDirty = false;
        }
        await readData();
      }
      return isSuccess;
    };

    const cancelChanges = async () => {
      substructureStore.resetPageData();
      await validate();
      inspectionLinksStore.isDirty = false;
    };

    let piers = computed(() => substructureStore.getPiers());

    const nearAbutmentSubstructureUnitKey = computed(() =>
      substructureStore.getSubStructureUnitKey(substructureValue.value, SUB_STRUCTURE_UNIT_KEY.NEAR_ABUTMENT),
    );
    const farAbutmentSubstructureUnitKey = computed(() =>
      substructureStore.getSubStructureUnitKey(substructureValue.value, SUB_STRUCTURE_UNIT_KEY.FAR_ABUTMENT),
    );

    let substructureConditionUpdated = ref(false);
    let conditionUpdated = ref(false);

    const updateDuration = () => {
      if (substructureConditionUpdated.value) {
        substructureValue.value.subRatingDurationChanges = inspectionStore.setDurationChanges(
          substructureValue.value.subRatingDurationChanges,
        );
        substructureValue.value.subRatingDuration = inspectionStore.setDuration(
          substructureValue.value.subRatingDuration,
          substructureValue.value.subRatingDurationChanges,
        );
      }
      substructureConditionUpdated.value = false;
      conditionUpdated.value = false;
    };

    const allPanels = [0];
    const panel = ref(allPanels);

    const setUpdatedComment = (comment, commentType) => {
      const foundComment = substructureValue.value.inspectionComments.find((c) => {
        return c.commentType === commentType;
      });
      if (foundComment) {
        foundComment.notes = comment.notes;
      } else {
        substructureValue.value.inspectionComments.push(comment);
      }
      handleUpdate();
    };

    let pierPanel = ref([]);

    const commentsMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.INSPECTION_COMMENT_MAX_LENGTH);
    });
    function validateUnderwaterInsp(structureUnit, subType) {
      if (subType == "abutment") {
        structureUnit = substructureValue.value.structureUnits?.find((unit) => unit.strUnitKey == structureUnit);
      }
      let uwInsp = substructureValue.value?.inspectionsUnderwater?.find(
        (element) => element?.strUnitKey == structureUnit.strUnitKey,
      );
      if (!uwInsp) return true;
      const requiredProps = [
        "obsScourRating",
        "changeSinceLastInsp",
        "scourHole",
        "debrisPotential",
        "subScour",
        "openAdeqChannel",
        "sedDeposit",
        "alignment",
        "veloStreamSlope",
        "subUnitType",
        "invFoundType",
        "streamBedMaterial",
        "underwaterInspType",
      ];
      const rangedRequiredProps = [
        { key: "obsScourDepth", min: 0, max: 99.9 },
        { key: "maxWaterDepth", min: 0, max: 99.9 },
      ];
      const rangedOptionalProps = [
        { key: "calcScourDepth100", min: 0, max: 99.9 },
        { key: "calcScourDepth500", min: 0, max: 99.9 },
      ];
      return (
        validateUnderWaterRequiredProps(uwInsp, requiredProps) &&
        validateUnderWaterRangedProps(uwInsp, rangedRequiredProps, true) &&
        validateUnderWaterRangedProps(uwInsp, rangedOptionalProps, false)
      );
    }
    const validateUnderWaterRequiredProps = (obj, props) => {
      return props.every((prop) => obj[prop] != null && obj[prop].toString().trim() !== "");
    };

    function validateUnderWaterRangedProps(obj, properties, isRequired) {
      return properties.every(({ key, min, max }) => {
        const val = obj[key];
        if (val == null || val.toString().trim() === "") {
          return !isRequired;
        }
        const num = Number(val);
        return valueInRangeRule(num, min, max) === true;
      });
    }
    async function validate() {
      let pierValidArray = [];
      let pierValid = false;
      let nearValid = false;
      let farValid = false;
      subConditions.value?.validate();
      if (nearAbutmentSubstructureUnitKey.value) {
        nearValid = validateUnderwaterInsp(nearAbutmentSubstructureUnitKey.value, "abutment");
      } else {
        nearValid = true;
      }
      if (farAbutmentSubstructureUnitKey.value) {
        farValid = validateUnderwaterInsp(farAbutmentSubstructureUnitKey.value, "abutment");
      } else {
        farValid = true;
      }

      if (piers.value.length > 0) {
        for (const pier of piers.value ?? []) {
          pierValidArray.push(validateUnderwaterInsp(pier, "pier"));
        }
        if (!pierValidArray.includes(false)) {
          pierValid = true;
        }
      } else {
        pierValid = true;
      }
      valid.value = pierValid && farValid && nearValid && validSubConditions.value;
    }

    const toggleAllPanels = (expandAll) => {
      isExpanded.value = expandAll;
      if (expandAll) {
        panel.value = allPanels;
        if (piers.value.length > 0) {
          pierPanel.value = [0];
        }
      } else {
        panel.value = [];
        pierPanel.value = [];
      }
    };

    const printD491 = () => {
      return { reportPage: "D491-Insp-Substructure-Screen", marker: SNACKBAR_MARKER.SUBSTRUCTURE };
    };

    const handleUpdate = () => {
      if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
        inspectionLinksStore.isDirty = true;
      } else if (props.moduleType === MODULE_TYPES.INSPECTION) {
        inspectionStore.setDirtyFlag(true);
      }
    };
    watch(
      () => [props],
      async () => {
        await readData();
      },
      { deep: true },
    );

    return {
      handleUpdate,
      MODULE_TYPES,
      setUpdatedComment,
      cancelChanges,
      saveChanges,
      readData,
      substructureValue,
      conditionUpdated,
      substructureConditionUpdated,
      updateDuration,
      validSubConditions,
      subConditions,
      valid,
      nearAbutmentSubstructureUnitKey,
      farAbutmentSubstructureUnitKey,
      validate,
      abutmentNearRef,
      abutmentFarRef,
      pierPanel,
      piers,
      commentsMaxLength,
      structure,
      REFERENCE_TABLE,
      getHistDuration,
      panel,
      inspectionStore,
      isEditable,
      INSPECTION_COMMENTS,
      SUB_STRUCTURE_UNIT_KEY,
      isExpanded,
      toggleAllPanels,
      printD491,
      valueInRangeRule,
    };
  },
  components: {
    ManagementCommentTypeTextField,
    CommentTypeTextField,
    LabelSelect,
    AbutmentComponent,
    PiersSubstructure,
    ExpandCollapseToggle,
    ValidationCountsHeader,
  },
};
</script>

<style scoped lang="scss">
@use "@/styles/colors" as c;

h3 {
  order: 1;
  color: c.$p-white !important;
}

.v-expansion-panel-title__icon {
  order: 0;
}

.tableHeader {
  text-align: center !important;
  background: c.$p-pa-light-blue !important;
}

.v-label.v-field-label {
  color: c.$p-black !important;
  opacity: 1 !important;
  font-weight: bold !important;
}
</style>
